import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import {
  Form,
  FormGroup,
  NumberStepper,
  RadioGroup,
  Range
} from 'components/common/form';
import { RadioGroupInput } from 'components/common/form/common';
import { EditorBlockTranslationReference } from 'components/editor';
import {
  EditorBlockCustomControls,
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo,
  EditorBlockTextField
} from 'components/editor/blocks';
import {
  ASPECT_RATIOS,
  DEFAULT_MIN_HEIGHT,
  DEFAULT_SCALE
} from './EditorBlockEmbedConstants';

import type { BlockEmbed, BlockH5P, BlockScorm, Course } from 'shared-types';
import type { BlockEmbedStateProps } from './EditorBlockEmbed';

type Props = BlockEmbedStateProps & {
  blockId: number;
  customControls?: React.ReactNode;
  disableIsMandatorySettings?: boolean;
  hideNonTranslatableSettings?: boolean;
  iframeWidth: number;
  isPreview?: boolean;
  languageVersion: Course['language'];
  updateData: (data) => void;
  blockType: 'embedcode' | 'embedlink' | 'h5p' | 'scorm';
  data: BlockEmbed | BlockH5P | BlockScorm;
};

const EditorBlockEmbedControls = ({
  blockId,
  blockType,
  customControls,
  data,
  disableIsMandatorySettings,
  hideNonTranslatableSettings = false,
  iframeWidth,
  isPreview = false,
  languageVersion,
  updateData,
  ...stateProps
}: Props) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const localeData = data?.locales?.[languageVersion];

  const handleFullScreenMessageChange = (value: string) => {
    updateLocale({
      translatable: {
        ...data.locales[languageVersion].translatable,
        openFullScreenMessage: value
      }
    });
  };

  const handleMinHeight = (value: number) => {
    const previousMinHeight = data.locales[languageVersion].minHeight
      ? data.locales[languageVersion].minHeight
      : DEFAULT_MIN_HEIGHT;

    const minHeight = previousMinHeight
      ? previousMinHeight + value
      : Math.abs(value);
    updateLocale({ minHeight });
  };

  const handleSetZoom = (scale: number) => {
    updateLocale({ scale: scale / 100, editorIframeSetZoomWidth: iframeWidth });
  };

  const handleToggleFullScreen = (value: string) => {
    updateLocale({ enableFullScreen: value });
  };

  const handleUpdateIframeAspectRatio = (aspectRatio: {
    label: string;
    value: number;
  }) => {
    updateLocale({ aspectRatio });
  };

  const handleUpdateNumberOfTasks = (tasks: number) => {
    updateLocale({ numberOfTasks: tasks });
  };

  const handleUseAspectRatioChange = (value: string) => {
    updateLocale({
      useAspectRatio: value === 'yes',
      aspectRatio: ASPECT_RATIOS[0]
    });
  };

  const updateLocale = (packet: { [key: string]: unknown }) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: { ...data.locales[languageVersion], ...packet }
      }
    });
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  const isH5PTask = blockType === 'h5p' && (data as BlockH5P)?.isTask;
  const isScormTask = blockType === 'scorm' && (data as BlockScorm)?.isTask;

  const defaultLocaleData = data?.locales?.[course.language];
  const referenceLocaleData = data?.locales?.[editorLanguageReference];

  return (
    <div className="settings">
      {hideNonTranslatableSettings ? (
        defaultLocaleData?.enableFullScreen === 'forced' ? (
          <FormGroup
            label={messages.editorBlockEmbedFullscreenOnlyDescriptionLabel}
            maxWidth="100%"
          >
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                referenceLocaleData?.translatable?.openFullScreenMessage || ''
              }
              translationHtml={
                localeData?.translatable.openFullScreenMessage || ''
              }
            >
              <EditorBlockTextField
                disabled={isPreview}
                onValueChange={handleFullScreenMessageChange}
                placeholder={messages.commonAddText}
                value={localeData?.translatable?.openFullScreenMessage || ''}
              />
            </EditorBlockTranslationReference>
          </FormGroup>
        ) : null
      ) : (
        <>
          {isH5PTask ? (
            <FormGroup
              label={messages.blockH5PNumberOfTasks}
              labelTooltip={messages.blockH5PNumberOfTasksInfo}
              style={{ marginBottom: 10 }}
            >
              <NumberStepper
                disabled={isPreview}
                field="numberOfTasks"
                id="number-of-tasks"
                initialValue={
                  'numberOfTasks' in localeData ? localeData?.numberOfTasks : 1
                }
                min={1}
                max={100}
                stepValue={1}
                onChange={handleUpdateNumberOfTasks}
              />
            </FormGroup>
          ) : null}
          <div className="row-md">
            <div className="col-min">
              <FormGroup
                label={messages.editorBlockEmbedSize}
                style={{ marginRight: 20 }}
              >
                <RadioGroupInput
                  onChange={handleUseAspectRatioChange}
                  options={[
                    {
                      disabled:
                        isPreview || localeData?.enableFullScreen === 'forced',
                      id: `editor-block-embed-controls-size-min-height-${blockId}`,
                      label: messages.editorBlockEmbedHeight,
                      value: 'no'
                    },
                    {
                      disabled:
                        isPreview || localeData?.enableFullScreen === 'forced',
                      id: `editor-block-embed-controls-size-aspect-ratio-${blockId}`,
                      label: messages.editorBlockEmbedAspectRatio,
                      value: 'yes'
                    }
                  ]}
                  value={localeData?.useAspectRatio ? 'yes' : 'no'}
                />
              </FormGroup>
            </div>

            <div className="col">
              {localeData?.useAspectRatio ? (
                <div className="embed-size-aspect-ratio">
                  <div className="embed-size-label">
                    {`${messages.editorBlockEmbedAspectRatio}: ${localeData.aspectRatio.label}`}
                  </div>

                  <div className="embed-aspect-ratio-buttons">
                    {ASPECT_RATIOS.map((ratio) => {
                      return (
                        <button
                          key={ratio.value}
                          className={`embed-size-button ${
                            localeData?.aspectRatio &&
                            ratio.value == localeData?.aspectRatio.value
                              ? 'embed-size-button-active'
                              : ''
                          }`}
                          disabled={
                            isPreview ||
                            localeData?.enableFullScreen === 'forced'
                          }
                          onClick={() => handleUpdateIframeAspectRatio(ratio)}
                          style={{
                            width: 60,
                            height: 'calc(' + ratio.value + ' * 60px)'
                          }}
                        >
                          {ratio.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="embed-size-min-height">
                  <div className="embed-size-label">
                    {`${messages.editorBlockEmbedHeight}: ${localeData?.minHeight}px`}
                  </div>

                  <Button
                    category="light"
                    disabled={
                      isPreview || localeData?.enableFullScreen === 'forced'
                    }
                    onClick={() => handleMinHeight(-50)}
                    size="small"
                    style={{ fontSize: '1.4rem', padding: '4px 8px 5px' }}
                    text={
                      <>
                        <Icon
                          icon="long-arrow-alt-up"
                          style={{ marginRight: 5 }}
                        />
                        {'50px'}
                      </>
                    }
                  />

                  <Button
                    category="light"
                    disabled={
                      isPreview || localeData?.enableFullScreen === 'forced'
                    }
                    onClick={() => handleMinHeight(50)}
                    size="small"
                    style={{ fontSize: '1.4rem', padding: '4px 8px 5px' }}
                    text={
                      <>
                        <Icon
                          icon="long-arrow-alt-down"
                          style={{ marginRight: 5 }}
                        />
                        {'50px'}
                      </>
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <FormGroup label={messages.editorBlockEmbedZoomLevel}>
            <div style={{ display: 'inline-block', marginRight: 5 }}>
              <Form
                key={`scale-form-${languageVersion}`}
                initialValues={{
                  scale: Math.round((localeData?.scale || DEFAULT_SCALE) * 100)
                }}
              >
                <Range
                  disabled={
                    isPreview || localeData?.enableFullScreen === 'forced'
                  }
                  field="scale"
                  id={`image-max-width-${blockId}`}
                  max={100}
                  min={50}
                  onChange={handleSetZoom}
                  showValue={false}
                  stepValue={5}
                  style={{ maxWidth: 80, width: 80 }}
                />
              </Form>
            </div>

            <div style={{ display: 'inline-block', marginRight: 5 }}>
              {`${Math.round((localeData?.scale || DEFAULT_SCALE) * 100)}%`}
            </div>
          </FormGroup>

          <EditorBlockMoreSettings
            settingsHaveBeenEdited={
              !isPreview &&
              Boolean(
                localeData?.enableFullScreen === 'enabled' ||
                  localeData?.enableFullScreen === 'forced' ||
                  ((isH5PTask || isScormTask) &&
                    (data as BlockH5P | BlockScorm)?.isMandatory)
              )
            }
          >
            <Form key={`fullscreen-form-${languageVersion}`}>
              <FormGroup label={messages.editorBlockEmbedFullscreen}>
                <RadioGroup
                  field={`enableFullScreen_${languageVersion}`}
                  initialValue={localeData?.enableFullScreen || 'disabled'}
                  onChange={handleToggleFullScreen}
                  options={[
                    {
                      disabled: isPreview,
                      id: `editor-multiplechoice-full-screen-option-disabled-${blockId}`,
                      label: messages.editorBlockEmbedDisableFullscreen,
                      value: 'disabled'
                    },
                    {
                      disabled: isPreview,
                      id: `editor-multiplechoice-full-screen-option-enabled-${blockId}`,
                      label: messages.editorBlockEmbedEnableFullscreen,
                      value: 'enabled'
                    },
                    {
                      disabled: isPreview,
                      id: `editor-multiplechoice-full-screen-option-forced-${blockId}`,
                      label: messages.editorBlockEmbedForceFullscreen,
                      tooltip: messages.editorBlockEmbedForceFullscreenTooltip,
                      value: 'forced'
                    }
                  ]}
                />
              </FormGroup>
            </Form>

            {localeData?.enableFullScreen === 'forced' ? (
              <FormGroup
                label={messages.editorBlockEmbedFullscreenOnlyDescriptionLabel}
                maxWidth="100%"
              >
                <EditorBlockTextField
                  disabled={isPreview}
                  onValueChange={handleFullScreenMessageChange}
                  placeholder={messages.commonAddText}
                  value={localeData?.translatable?.openFullScreenMessage || ''}
                />
              </FormGroup>
            ) : null}

            {!isEditingLanguageVersion && (isH5PTask || isScormTask) ? (
              <EditorBlockIsMandatorySettings
                blockId={blockId}
                data={data as BlockH5P | BlockScorm}
                disabled={isPreview || disableIsMandatorySettings}
                updateData={updateData}
              />
            ) : null}
          </EditorBlockMoreSettings>
        </>
      )}

      {isH5PTask || isScormTask ? (
        <EditorBlockRequireAnswerToProceedInfo
          data={data as BlockH5P | BlockScorm}
        />
      ) : null}

      {!isPreview && customControls ? (
        <EditorBlockCustomControls>{customControls}</EditorBlockCustomControls>
      ) : null}

      <style jsx>{`
        .settings {
          margin-top: 20px;
        }
        .embed-size-min-height {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .embed-size-label {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 600;
          margin-right: 5px;
        }
        .embed-size-button {
          background-color: ${theme.lightGreyDark};
          border: 1px solid transparent;
          border-radius: 3px;
          box-shadow: none;
          color: ${theme.textColorLight};
          cursor: pointer;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          margin: 5px;
          outline: 0;
          padding: 5px;
          text-align: center;
          transition: background-color 0.3s, border-color 0.3s;
        }
        .embed-size-button:focus:not(:disabled),
        .embed-size-button:hover:not(:disabled),
        .embed-size-button-active {
          border-color: ${theme.textColor};
          background-color: ${theme.lightGreyDarkActive};
          color: ${theme.textColor};
        }
        .embed-size-button:disabled {
          cursor: default;
          opacity: 0.7;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockEmbedControls;
